/**
 * useLiveOutcomesPolling.ts - Live Outcomes Polling Hook
 * Phase 1A - Live Outcomes Implementation
 * 
 * ARCHITECT DIRECTIVE:
 * - Separate hook from useLeaderboardPolling (no coupling)
 * - Route-owned polling only
 * - Stop when all props resolved
 * - Respect VITE_DISABLE_POLLING kill switch
 * 
 * D1 SAFETY COMPLIANCE:
 * ✅ Clear interval on unmount
 * ✅ Skip if loading guard (no overlapping requests)
 * ✅ Abort/ignore stale responses on pool change or tab leave
 * ✅ Stop polling when all slots resolved
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const POLL_INTERVAL = 60000; // 60 seconds - slower than leaderboard per risk register
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

interface Resolution {
  status: string | null;  // 'pending' | 'hit' | 'miss' | 'push' | 'void' (or legacy 'win' | 'loss')
  payout: number | null;
  actualValue: number | null;
  resolvedAt: string | null;
}

interface RosterSlot {
  slot: number;
  label: string;
  player: { id: number; name: string; team: string; pos: string } | null;
  prop: { id: number; stat: string; line: number } | null;
  side: string | null;
  units: number;
  resolution: Resolution | null;
}

interface EntryResponse {
  entry: {
    roster: RosterSlot[];
    totalUnits: number;
    status: string;
    score: number | null;
    rank: number | null;
    lockedAt: string | null;
    portfolioId: string;
    lastUpdated: string | null;
    resolvedCount: number;
    totalSlots: number;
  } | null;
  state: string;
}

interface UseLiveOutcomesPollingResult {
  roster: RosterSlot[];
  resolvedCount: number;
  totalSlots: number;
  score: number | null;
  rank: number | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date | null;
  isPolling: boolean;
  togglePolling: () => void;
  isFullyResolved: boolean;
}

export function useLiveOutcomesPolling(
  poolId: string | undefined,
  userId: string | undefined
): UseLiveOutcomesPollingResult {
  // State
  const [roster, setRoster] = useState<RosterSlot[]>([]);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [totalSlots, setTotalSlots] = useState(7);
  const [score, setScore] = useState<number | null>(null);
  const [rank, setRank] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isPolling, setIsPolling] = useState(true);
  const [isFullyResolved, setIsFullyResolved] = useState(false);
  
  // Refs for D1 safety
  const isLoadingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const poolIdRef = useRef(poolId);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedOnceRef = useRef(false);
  
  // Update ref when poolId changes
  useEffect(() => {
    poolIdRef.current = poolId;
    hasLoadedOnceRef.current = false;
  }, [poolId]);
  
  /**
   * Fetch entry data with resolution status
   */
  const fetchOutcomes = useCallback(async (force: boolean = false) => {
    // D1: Skip if already loading
    if (!force && isLoadingRef.current) {
      console.log('[LiveOutcomes] Skipping fetch - already loading');
      return;
    }
    
    if (!poolId || !userId) {
      setError('No pool ID or user ID provided');
      setLoading(false);
      return;
    }
    
    // D1: Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    const currentPoolId = poolId;
    
    isLoadingRef.current = true;
    if (!hasLoadedOnceRef.current) setLoading(true);
    
    try {
      const response = await fetch(
        `${API_BASE}/pools/${poolId}/entry`,
        {
          signal: abortControllerRef.current.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': userId
          }
        }
      );
      
      // D1: Check if pool changed during request
      if (poolIdRef.current !== currentPoolId) {
        console.log('[LiveOutcomes] Ignoring stale response - pool changed');
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data: EntryResponse = await response.json();
      
      // D1: Final check before updating state
      if (poolIdRef.current !== currentPoolId) {
        return;
      }
      
      if (data.entry) {
        setRoster(data.entry.roster);
        
        // FIX: Calculate resolved count from roster data instead of trusting backend
        // Supports both old (WIN/LOSS) and new (HIT/MISS) labels
        const calculatedResolvedCount = data.entry.roster.filter(
          (slot: RosterSlot) => slot.resolution?.status && 
            ['hit', 'miss', 'push', 'win', 'loss'].includes(slot.resolution.status.toLowerCase())
        ).length;
        setResolvedCount(calculatedResolvedCount);
        
        setTotalSlots(data.entry.totalSlots || data.entry.roster.length);
        setScore(data.entry.score);
        setRank(data.entry.rank);
        
        // Check if fully resolved - stop polling if so
        // Use calculated count for accurate check
        const fullyResolved = calculatedResolvedCount >= (data.entry.totalSlots || data.entry.roster.length);
        setIsFullyResolved(fullyResolved);
        
        if (fullyResolved) {
          console.log('[LiveOutcomes] All slots resolved - stopping poll');
          setIsPolling(false);
        }
        
        if (data.entry.lastUpdated) {
          setLastUpdated(new Date(data.entry.lastUpdated));
        } else {
          setLastUpdated(new Date());
        }
      }
      
      setError(null);
      hasLoadedOnceRef.current = true;
      
    } catch (err) {
      // D1: Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('[LiveOutcomes] Request aborted');
        return;
      }
      
      if (poolIdRef.current === currentPoolId) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [poolId, userId]);
  
  /**
   * Manual refresh (bypasses loading guard)
   */
  const refresh = useCallback(() => {
    fetchOutcomes(true);
  }, [fetchOutcomes]);
  
  /**
   * Toggle polling on/off
   */
  const togglePolling = useCallback(() => {
    setIsPolling(prev => !prev);
  }, []);
  
  /**
   * Initial fetch on mount/pool change
   */
  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setRoster([]);
    setResolvedCount(0);
    setScore(null);
    setRank(null);
    setLastUpdated(null);
    setError(null);
    setIsFullyResolved(false);
    
    fetchOutcomes(true);
  }, [poolId, userId]);
  
  /**
   * Polling with interval cleanup
   */
  useEffect(() => {
    const disablePolling = import.meta.env.VITE_DISABLE_POLLING === 'true';
    
    // Stop conditions per risk register
    if (!isPolling || disablePolling || !poolId || isFullyResolved) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    
    intervalRef.current = setInterval(() => {
      fetchOutcomes();
    }, POLL_INTERVAL);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPolling, poolId, isFullyResolved, fetchOutcomes]);
  
  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  
  /**
   * Pause polling when tab is hidden
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      } else if (!isFullyResolved) {
        fetchOutcomes(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchOutcomes, isFullyResolved]);
  
  return {
    roster,
    resolvedCount,
    totalSlots,
    score,
    rank,
    loading,
    error,
    refresh,
    lastUpdated,
    isPolling,
    togglePolling,
    isFullyResolved
  };
}

export default useLiveOutcomesPolling;